import { NavLink, useLocation } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { userCanAccessFeature } from '@/lib/plan-access';
import { useProfile } from '@/lib/profile-context';
import { accountsAPI } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard, Activity, History, ShieldCheck, FileText,
  Users, Settings, LogOut, Package, ListTodo, Shield as ShieldIcon, Store, ClipboardList, UserCog,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { ProtegoLogo } from '@/components/brand/ProtegoLogo';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const navItems = [
  { section: 'Dashboard', items: [
    { to: '/', icon: LayoutDashboard, label: 'Overview', end: true, feature: 'monitor' },
  ]},
  { section: 'INVESTIGATE', items: [
    { to: '/signals', icon: Activity, label: 'Signals', feature: 'monitor' },
    { to: '/history', icon: History, label: 'History / Pre-Emptive', feature: 'monitor' },
    { to: '/alerts', icon: ShieldCheck, label: 'Alerts', feature: 'monitor' },
    { to: '/metrics', icon: FileText, label: 'Metrics', feature: 'monitor' },
    { to: '/asin-risks', icon: Package, label: 'ASINs at Risk', feature: 'monitor' },
  ]},
  { section: 'ACTIONS', items: [
    { to: '/tasks', icon: ListTodo, label: 'Tasks', feature: 'monitor' },
    { to: '/poa-templates', icon: ShieldIcon, label: 'POA Templates', feature: 'poa' },
  ]},
  { section: 'YOUR PLAN', items: [
    { to: '/audit-report', icon: FileText, label: 'Account Health Audit', feature: 'audit_report', hideIfMonitor: true },
  ]},
  { section: 'CONNECT', items: [
    { to: '/accounts', icon: Store, label: 'Amazon Accounts' },
    { to: '/support-data', icon: ClipboardList, label: 'Seller Central scraped', feature: 'monitor' },
  ]},
  { section: 'MANAGE', items: [
    { to: '/admin', icon: Users, label: 'Admin', adminOnly: true },
    { to: '/users', icon: UserCog, label: 'Team', adminOnly: true },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ]},
];

export function Sidebar({ currentSeller, onClose, sellers, onSellerChange }) {
  const { user, logout, isAdmin } = useAuth();
  const { accounts, accountId, profileId, currentAccount, currentProfile, selectProfile, hasSelection } = useProfile();
  const location = useLocation();
  const [profilesByAccount, setProfilesByAccount] = useState({});
  const [profilesLoading, setProfilesLoading] = useState(false);

  const loadProfilesForAccounts = useCallback(async () => {
    if (!Array.isArray(accounts) || accounts.length === 0) {
      setProfilesByAccount({});
      return;
    }
    setProfilesLoading(true);
    try {
      const entries = await Promise.all(accounts.map(async (a) => {
        try {
          const res = await accountsAPI.getProfiles(a.id);
          const data = res.data?.data || res.data || [];
          return [String(a.id), Array.isArray(data) ? data : []];
        } catch {
          return [String(a.id), []];
        }
      }));
      setProfilesByAccount(Object.fromEntries(entries));
    } finally {
      setProfilesLoading(false);
    }
  }, [accounts]);

  const accountProfileOptions = useMemo(() => {
    return (accounts || []).flatMap((a) => {
      const accountProfiles = profilesByAccount[String(a.id)] || [];
      return accountProfiles.map((p) => ({
        value: `${a.id}:${p.id}`,
        label: `${a.accountName || a.name || `Account ${a.id}`} -> ${p.name || `Profile ${p.id}`}${p.countryCode ? ` (${p.countryCode})` : ''}`,
        accountId: String(a.id),
        profileId: String(p.id),
      }));
    });
  }, [accounts, profilesByAccount]);

  const combinedValue = accountId && profileId ? `${accountId}:${profileId}` : '';

  const handleAccountProfileChange = (value) => {
    const [nextAccountId, nextProfileId] = String(value).split(':');
    if (!nextAccountId || !nextProfileId) return;
    selectProfile(nextAccountId, nextProfileId);
    if (onSellerChange) {
      const seller = sellers?.find(s => String(s.account_id) === String(nextAccountId) && String(s.profile_id) === String(nextProfileId));
      if (seller) onSellerChange(seller.id);
    }
  };

  return (
    <div className="flex flex-col h-full bg-card border-r border-border/80 w-[264px]" data-testid="sidebar">
      <div className="px-5 py-5">
        <ProtegoLogo size="sm" />
      </div>

      {/* Account & Profile Selector */}
      <div className="mx-4 mb-3 space-y-2">
        {accounts?.length > 0 && (
          <Select
            value={combinedValue}
            onValueChange={handleAccountProfileChange}
            onOpenChange={(open) => {
              if (open && Object.keys(profilesByAccount).length === 0) {
                loadProfilesForAccounts();
              }
            }}
          >
            <SelectTrigger className="w-full h-9 text-sm bg-muted/40 border-border/60" data-testid="sidebar-account-profile-switcher">
              <div className="flex items-center gap-2 truncate">
                <Store className="w-3.5 h-3.5 text-[hsl(188,100%,55%)] shrink-0" />
                <SelectValue placeholder={profilesLoading ? 'Loading profiles…' : 'Select account + profile'} />
              </div>
            </SelectTrigger>
            <SelectContent>
              {profilesLoading && accountProfileOptions.length === 0 ? (
                <SelectItem value="_loading" disabled>Loading…</SelectItem>
              ) : (
                accountProfileOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        )}

        {accountId && !profileId && !currentSeller && (
          <p className="text-xs text-muted-foreground px-1">No profiles for this account</p>
        )}

        {currentSeller && (
          <div className="flex items-center gap-1.5 px-1">
            {currentSeller.marketplace && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground">{currentSeller.marketplace}</span>}
            {currentSeller.model && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground">{currentSeller.model}</span>}
            {currentSeller.health_score != null && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                currentSeller.health_score >= 75 ? 'bg-emerald-500/10 text-emerald-400' :
                currentSeller.health_score >= 50 ? 'bg-yellow-500/10 text-yellow-400' :
                'bg-red-500/10 text-red-400'
              }`}>
                Protego: {Math.round(currentSeller.health_score ?? currentSeller.protego_score ?? 0)}
              </span>
            )}
          </div>
        )}
      </div>

      {!hasSelection && (
        <div className="mx-4 mb-3 px-3 py-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
          <p className="text-xs text-yellow-400 font-medium">Select an account & profile</p>
          <p className="text-[10px] text-yellow-400/70 mt-0.5">Choose from the dropdowns above or the Accounts page to get started.</p>
        </div>
      )}

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {navItems.map((section) => {
          const visibleItems = section.items.filter((item) => {
            if (item.adminOnly && !isAdmin) return false;
            if (item.feature && !userCanAccessFeature(user, item.feature)) return false;
            if (item.hideIfMonitor && userCanAccessFeature(user, 'monitor')) return false;
            // Admins can open Team/Admin without selecting a profile first
            if (isAdmin && item.adminOnly) return true;
            if (item.to === '/accounts' || item.to === '/settings' || item.to === '/audit-report' || item.to === '/poa-templates') return true;
            if (!hasSelection) return false;
            return true;
          });
          if (visibleItems.length === 0) return null;
          return (
          <div key={section.section} className="mb-4">
            <p className="px-3 py-2 text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
              {section.section}
            </p>
            {visibleItems.map((item) => {
              const isActive = item.end
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                    isActive
                      ? 'text-foreground bg-muted/60 shadow-[0_1px_0_0_hsl(222_16%_16%/0.7)]'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                  )}
                >
                  <item.icon className="w-4 h-4 opacity-90" />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      <Separator className="bg-border/60" />

      <div className="px-4 py-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-muted text-sm font-medium">
              {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {user?.role === 'admin' || user?.role === 'superadmin' ? 'Admin' : user?.role === 'manager' ? 'Manager' : 'Seller'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            data-testid="sidebar-logout-button"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
